/* Copyright (c) 2019-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

export enum SpanTagNames {
    HttpUrl = 'http.url',
    HttpStatusCode = 'http.status_code',
    HttpStatusCodeFamily = 'http.status_code_family',
    HttpMethod = 'http.method',
    HttpHostname = 'http.hostname',
    HttpHeadersPrefix = 'http.headers.',
    HttpPath = 'http.path',
    HttpErrorText = 'http.error_text',
    ParamsPrefix = 'params.',
    Error = 'error',
    SpanKind = 'span.kind',
    Component = 'component',
    ClientOs = 'client.os',
    ClientOsName = 'client.os.name',
    ClientOsVersion = 'client.os.version',
    ServiceVersion = 'service.version',
    ServiceType = 'service.type',
    Type = 'type',

    DeviceId = 'deviceId',
    AppName = 'appName',
    LayoutExecutionId = 'layoutExecutionId',
    Origin = 'origin',
    VpcId = 'vpcId',
    Locale = 'locale',
    Trigger = 'trigger',
    LoginStatus = 'loginStatus',
    CmsId = 'cmsId',
    FailurePoint = 'failurePoint',
    ErrorCode = 'errorCode',
    ErrorType = 'errorType',
    NetworkOverridePresent = 'networkOverridePresent',
    NetworkTestStatus = 'networkTestStatus',
    ZoneName = 'zone.name',
    ZoneAddress = 'zone.address',
    OobeDialogShown = 'oobeDialogShown',
    LoginWindowShown = 'loginWindowShown',
    LaunchSource = 'launchSource',
    PlayType = 'playType',
    ShortName = 'shortName',
    UsedLatencyBasedRouting = 'usedLatencyBasedRouting',
    IsSessionAutoResumed = 'isSessionAutoResumed',
    ErrorDetails = 'errorDetails',
    RoutingOverrideUsed = 'routingOverrideUsed',
    OverrideZoneName = 'overrideZoneName',
    OverrideZoneAddress = 'overrideZoneAddress',
    SessionId = 'sessionId',
    SkippedAuthCheck = 'skippedAuthCheck',
    CountryCode = 'countryCode',
    FastestJarvisDomain = 'fastestJarvisDomain',
    TraceId = 'traceId',
    RequestId = 'requestId',
    PanelType = 'panelType',
    BeforeEventDuration = 'beforeEventDuration',
    PresentDuration = 'presentDuration',
    PushMessageType = 'pushMessageType',
    AccountLinkingProvider = 'accountLinkingProvider',
    HasFilters = 'hasFilters',
    HasSearchQuery = 'hasSearchQuery',
    IsFirstPage = 'isFirstPage',
    ErrorMessage = 'errorMessage',
    ErrorStack = 'errorStack',
    DevMode = 'devMode',
    PanelName = 'panelName',
    SectionId = 'sectionId',
    SectionTitle = 'sectionTitle',
    LoginMethod = 'loginMethod',
    LoginResult = 'loginResult',
    LoginErrorCode = 'loginErrorCode',
    LoginErrorUrl = 'loginErrorUrl',
    LoginIdpId = 'loginIdpId',
    GameFound = 'gameFound',
    OldSubscriptionState = 'oldSubscriptionState',
    NewSubscriptionState = 'newSubscriptionState',
    EntitlementStatus = 'entitlementStatus',
    ProviderName = 'providerName',
    IsLimitedState = 'isLimitedState',
    UserId = 'userId',
    LatencyMs = 'latencyMs',
    Context = 'context',
    MembershipStatus = 'membershipStatus',
    ExpirationStatus = 'expirationStatus',
    SyncState = 'syncState',
    SyncStatus = 'syncStatus',
    ApiName = 'apiName',
    ServiceUseCase = 'serviceUseCase',
    ProductUri = 'productUri',
    ProductSku = 'productSku',
    IsUserOnline = 'isUserOnline',
    LiveDataSuccessful = 'liveDataSuccessful',
    SectionsCount = 'sectionsCount',
    OwnershipFlowType = 'ownership.flowType',
    OwnershipEntryPoint = 'ownership.entryPoint',
    OwnershipGameId = 'ownership.gameId',
    OwnershipGameShortName = 'ownership.gameShortName',
    OwnershipStage = 'ownership.stage',
    OwnershipFlowEnd = 'ownership.flowEnd',
    OwnershipMethod = 'ownership.method',
    OwnershipSelectedPlatformId = 'ownership.selectedPlatformId',
    OwnershipStoreId = 'ownership.storeId',
    OwnershipSyncResult = 'ownership.syncResult',
    OwnershipGameFound = 'ownership.gameFound',
    OwnershipUserAction = 'ownership.userAction',
    OwnershipErrorType = 'ownership.errorType',
    ApiResponse = 'apiResponse',
    SdkVersion = 'sdkVersion'
}

export enum SpanNames {
    DelegateToken = 'GetDelegateToken',
    ClientGetLayout = 'ClientGetLayout',
    FetchLayout = 'LayoutManagerGetLayout',
    GetServerInfo = 'GetServerInfo',
    GetSpecificServerInfo = 'GetSpecificServerInfo',
    ClientGetAppMetadata = 'ClientGetAppMetadata',
    ClientGetAppsList = 'ClientGetAppsList',
    FetchAppsList = 'LayoutManagerGetAppsList',
    FetchAppMetadata = 'LayoutManagerGetAppMetadata',
    FetchSubscriptions = 'FetchSubscriptions',
    FetchUserProducts = 'FetchUserProducts',
    GetEntitlementStatus = 'GetEntitlementStatus',
    GameLaunch = 'GameLaunch',
    ClientGetSubscriptions = 'ClientGetSubscriptions',
    GetActiveSessions = 'GetActiveSessions',
    GetSessionInfo = 'GetSessionInfo',
    StartStreaming = 'StartStreaming',
    GetUserEntitlements = 'GetUserEntitlements',
    UpdateCheck = 'UpdateCheck',
    PreStreaming = 'PreStreaming',
    StreamerAuthentication = 'StreamerAuthentication',
    StreamerAuthorization = 'StreamerAuthorization',
    StreamerNetworktest = 'StreamerNetworkTest',
    SessionLoading = 'SessionLoading',
    GetRatingsInfo = 'GetRatingsInfo',
    GxTargetGetFeature = 'GxTargetGetFeature',
    GetFastestDomain = 'GetFastestDomain',
    JarvisPing = 'JarvisPing',
    JarvisOAuth = 'JarvisOAuth',
    StarfleetOAuth = 'StarfleetOAuth',
    LcarsFetchQueryData = 'LcarsFetchQueryData',
    LcarsPostQueryData = 'LcarsPostQueryData',
    ClientGetSurvey = 'ClientGetSurvey',
    ClientDismissSurvey = 'ClientDismissSurvey',
    PlatformSelection = 'PlatformSelection',
    PlatformSelectionSharedStorageRead = 'PlatformSelectionSharedStorageRead',
    PlatformSelectionDialogInteraction = 'PlatformSelectionDialogInteraction',
    AccountLinking = 'AccountLinking',
    AccountLinkingDialogInteraction = 'AccountLinkingDialogInteraction',
    AlsGetLoginUrl = 'AlsGetLoginUrl',
    AlsPostLoginUrl = 'AlsPostLoginUrl',
    AlsPostToken = 'AlsPostToken',
    AlsSync = 'AlsSync',
    AlsDeleteLinking = 'AlsDeleteLinking',
    AlsUserLogin = 'AlsUserLogin',
    AlsTokenExchange = 'AlsTokenExchange',
    AlsUIConnect = 'AlsUIConnect',
    AlsUIDisconnect = 'AlsUIDisconnect',
    AlsUIReconnect = 'AlsUIReconnect',
    AlsUIResync = 'AlsUIResync',
    GetAemReleaseHighlights = 'GetAemReleaseHighlights',
    GetGfwslInformation = 'GetGfwslInformation',
    StarfleetDeleteClientToken = 'StarfleetDeleteClientToken',
    StarfleetGetClientToken = 'StarfleetGetClientToken',
    StarfleetGetAccessToken = 'StarfleetGetAccessToken',
    StarfleetGetUserInfo = 'StarfleetGetUserInfo',
    SendTelemetryEvent = 'SendTelemetryEvent',
    ClientSettings = 'ClientSettings',
    ScheduledMaintenance = 'ScheduledMaintenance',
    ScheduledMaintenanceSessionEndError = 'ScheduledMaintenanceSessionEndError',
    ClientGetCloudVariable = 'ClientGetCloudVariable',
    ClientStreamingQuality = 'ClientStreamingQuality',
    PushNotificationRegistration = 'PushNotificationRegistration',
    KVStoreGetClientData = 'KVStoreGetClientData',
    KVStoreSetClientData = 'KVStoreSetClientData',
    KVStoreDeleteClientData = 'KVStoreDeleteClientData',
    KVStoreAppendClientData = 'KVStoreAppendClientData',
    ClientGetPanel = 'ClientGetPanel',
    ClientDesktopPaste = 'ClientDesktopPaste',
    ClientClipboardAvailable = 'ClientClipboardAvailable',
    BackgroundNetworkTest = 'BackgroundNetworkTest',
    GetRemoteConfigV2 = 'GetRemoteConfigV2',
    PushMessageReceived = 'PushMessageReceived',
    PnsRegistration = 'PnsRegistration',
    PnsUnregister = 'PnsUnregister',
    UserAccountData = 'UserAccountData',
    SessionLengthWarning = 'SessionLengthWarning',
    VrsGetRewards = 'VrsGetRewards',
    UpsellMessages = 'UpsellMessages',
    CampaignsInfo = 'CampaignsInfo',
    SearchFilterGames = 'SearchFilterGames',
    UasAcquireUser = 'UasAcquireUser',
    AdToken = 'AdToken',
    AdSession = 'AdSession',
    AdOpportunity = 'AdFetch',
    AdStart = 'AdStart',
    ImaSdkLoad = 'ImaSdkLoad',
    AdPodSession = 'AdPodSession',
    FilterGroupSortOrderDefinitions = 'FilterGroupSortOrderDefinitions',
    GetLoginWallData = 'GetLoginWallData',
    GetLoginWallStringsOnly = 'GetLoginWallStringsOnly',
    AddFavoriteApp = 'AddFavoriteApp',
    RemoveFavoriteApp = 'RemoveFavoriteApp',
    AddOwnedVariant = 'AddOwnedVariant',
    RemoveOwnedVariant = 'RemoveOwnedVariant',
    SelectOwnedVariant = 'SelectOwnedVariant',
    GetUdsSummonedReport = 'GetUdsSummonedReport',
    GetUdsEndOfSessionReport = 'GetUdsEndOfSessionReport',
    OverallGfnSupportedLanguages = 'OverallGfnSupportedLanguages',
    GFNUIException = 'GFNUIException',
    GFNUIExceptionRenderDirective = 'GFNUIExceptionRenderDirective',
    OffDeviceLogin = 'OffDeviceLogin',
    LoginAttempt = 'LoginAttempt',
    UpdateLoginHint = 'UpdateLoginHint',
    LoginHintGetSsoInfo = 'LoginHintGetSsoInfo',
    LoginWallProviderInfo = 'LoginWallProviderInfo',
    LoginWallDataFetch = 'LoginWallDataFetch',
    LoginWallDeeplink = 'LoginWallDeeplink',
    LoginWallRedirect = 'LoginWallRedirect',
    StoreGameClients = 'StoreGameClients',
    EntitlementCheck = 'EntitlementCheck',
    SubscriptionStateTransition = 'SubscriptionStateTransition',
    SubscriptionDataRefresh = 'SubscriptionDataRefresh',
    StreamingQualityFetch = 'StreamingQualityFetch',
    NesApiCall = 'NesApiCall',
    EcommerceServiceCheck = 'EcommerceServiceCheck',
    ProviderSync = 'ProviderSync',
    AccountStateCheck = 'AccountStateCheck',
    OAuthTokenValidation = 'OAuthTokenValidation',
    MembershipFetch = 'MembershipFetch',
    LayoutInfo = 'layoutInfo',
    FetchAppsPatchInfoWithLibraryFilter = 'FetchAppsPatchInfoWithLibraryFilter',
    OwnershipFlow = 'OwnershipFlow',
    NativeClientDownloadBannerInteraction = 'NativeClientDownloadBannerInteraction',
    IapApiCall = 'IapApiCall',
    GrantFreeEntitlement = 'GrantFreeEntitlement',
    FetchProductMatrixFeatures = 'FetchProductMatrixFeatures',
    FetchPaymentStrings = 'FetchPaymentStrings',
    RestorePurchases = 'RestorePurchases',
    GetStoreSubscriptions = 'GetStoreSubscriptions',
    GetStoreUserLink = 'GetStoreUserLink',
    IapPurchaseFlow = 'IapPurchaseFlow',
    IapRestoreFlow = 'IapRestoreFlow',
    IapSilentRestoreFlow = 'IapSilentRestoreFlow'
}

export enum ComponentNames {
    XmlHttpRequest = 'XMLHttpRequest'
}

export enum ServiceTypeNames {
    Pc_A7 = 'pc-a7',
    Native_Windows = 'native-windows'
}

export enum SpanKindNames {
    Client = 'client'
}

export enum ErrorMessageNames {
    VpcId = 'ERROR_WHILE_GETTING_VPCID',
    UpdateSsoLoginInfoEntryError = 'UPDATE_SSO_LOGIN_INFO_ENTRY_ERROR',
    GetSsoLoginInfoError = 'GET_SSO_LOGIN_INFO_ERROR'
}

export enum BannerActionNames {
    Shown = 'banner_shown',
    Download = 'banner_download',
    Dismiss = 'banner_dismiss'
}

export const ServiceWorkerSpanPrefix = 'SW ';
